import { ChevronDown, Download, RefreshCw } from "lucide-react";
import { Button } from "@/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import type { PublicGalleryAlbum } from "@/features/gallery/types";

// Bộ điều khiển của màn album khách — trình bày lại theo dự án PHOTO (CEO chốt 27/08/2026).
//
// Bốn khối, từ trên xuống:
//  1. `GalleryStickyHeader` — thanh DÍNH duy nhất khi cuộn: tên album (serif) + bộ đếm ♥ + dải
//     bước chọn. Mọi thứ khác trôi đi cùng trang; giữ đúng luật PHOTO "thanh trên gọn".
//  2. `SelectionStageStrip` — "Nháp → Cuối", khách luôn biết trái tim của mình đang ghi vào bước
//     nào. Đây là cách PHOTO trình bày vòng chọn, áp lên hai lớp nháp/cuối sẵn có của GNPF.
//  3. `CustomerAlbumStrip` — CHỈ album con do studio tự đặt. Xem ghi chú trong chính hàm đó.
//  4. `CustomerGalleryToolbar` — MỘT dòng chip lọc + sắp xếp + làm mới + tải. KHÔNG còn nút "Chọn
//     nhiều": trái tim luôn bấm được, không cần chế độ riêng (bài học PHOTO — mỗi ảnh 1 nút).
//
// CEO chốt 27/08/2026 (khách chính của studio là U40–50): trước đây có HAI dòng chip nhìn y hệt
// nhau — dòng album con và dòng lọc — cùng mở đầu bằng chữ "Tất cả" và trùng nghĩa ở 3 chỗ nữa
// ("Ảnh chọn nháp"/"Chọn nháp", "Ảnh chọn cuối"/"Chọn cuối", "Ảnh final"/"Final"). Nay còn ĐÚNG
// một dòng bốn chip, chữ thuần Việt, không có "final". Từ 28/09/2026 (QĐ 0238 của GNPF) vòng 1
// đọc là "Chọn nháp" — cùng chữ với nhân viên.
//
// Chip cao tối thiểu 44px dưới màn nhỏ (min-h-11 lg:min-h-9) — luật chạm của app dừng ở 767px
// nhưng bài kiểm e2e đo tới DƯỚI 1024 — nên sàn 44px giữ tới lg, không phải md.

export type GalleryFilter = "all" | "recommended" | "draft" | "selected-final" | "final";
export type GallerySort = "newest" | "oldest" | "name-asc" | "name-desc";
export type GalleryCounts = Record<GalleryFilter, number>;
export type SelectionStage = "draft" | "final";

/** Nút tải: cả album, hay chỉ những tấm khách đã thả tim ở từng vòng. */
export type DownloadScope = "all" | "draft" | "final";

/**
 * Chữ chung cho hai vòng chọn — đổi ở đây là đổi khắp màn khách.
 *
 * CEO chốt 28/09/2026 (QĐ 0238 của GNPF, đảo chốt 27/08/2026): khách đọc CÙNG chữ với nhân viên —
 * vòng 1 là "Chọn nháp", vòng 2 là "Chốt ảnh". Vòng 1 vẫn là vòng lọc THẬT: khách lọc cả album
 * xuống một tập gọn rồi chốt hẳn ở vòng 2. Số ảnh mỗi vòng KHÔNG bị giới hạn.
 */
export const ROUND = {
  draft: { step: "Vòng 1 · Chọn nháp", short: "Vòng 1" },
  final: { step: "Vòng 2 · Chốt ảnh", short: "Vòng 2" },
} as const;

/**
 * Bốn chip khách nhìn thấy. Chip ảnh khách chọn luôn là danh sách của VÒNG ĐANG ĐỨNG, nên
 * khách chỉ phải hiểu một khái niệm thay vì hai chip nháp/cuối cạnh nhau.
 *
 * Sang vòng 2 thì bộ chip đảo lại theo đúng việc khách đang làm — lọc tiếp từ tập vòng 1 xuống:
 * chip đầu là "Ảnh vòng 1" (tập đang lọc, cũng là thứ trang tự mở khi vào vòng 2), và chip "Tất
 * cả" đổi tên thành "Toàn bộ album" để nói thật rằng bấm vào là ra khỏi tập đang lọc.
 */
function buildFilters(stage: SelectionStage, canSelect: boolean) {
  if (stage === "final" && canSelect) {
    return [
      { value: "draft" as GalleryFilter, label: `Ảnh ${ROUND.draft.short}` },
      { value: "selected-final" as GalleryFilter, label: "Ảnh đã chốt" },
      { value: "recommended" as GalleryFilter, label: "Studio gợi ý" },
      { value: "all" as GalleryFilter, label: "Toàn bộ album" },
    ];
  }
  return [
    { value: "all" as GalleryFilter, label: "Tất cả" },
    { value: "recommended" as GalleryFilter, label: "Studio gợi ý" },
    ...(canSelect ? [{ value: "draft" as GalleryFilter, label: "Ảnh tôi chọn nháp" }] : []),
    { value: "final" as GalleryFilter, label: "Ảnh hoàn thiện" },
  ];
}

const chipClass = (active: boolean) =>
  `inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm transition-colors lg:min-h-9 ${
    active
      ? "border-transparent bg-primary text-primary-foreground"
      : "border-border bg-background text-muted-foreground hover:text-foreground"
  }`;

/** Thanh dính duy nhất của trang: tên album + bộ đếm ♥ + dải bước chọn. */
export function GalleryStickyHeader({
  title,
  draftCount,
  finalCount,
  showCounter,
  stage,
  showStage,
  locked,
  onStageChange,
  onCounterClick,
}: {
  title: string;
  draftCount: number;
  finalCount: number;
  /** Có quyền chọn ảnh thì mới có bộ đếm — album "chỉ xem" không đếm gì cả. */
  showCounter: boolean;
  stage: SelectionStage;
  showStage: boolean;
  locked: boolean;
  onStageChange: (stage: SelectionStage) => void;
  onCounterClick: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2">
        <h2
          className="min-w-0 flex-1 truncate text-lg"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {title}
        </h2>
        {showCounter ? (
          <button
            type="button"
            aria-label={`Đã chọn nháp ${draftCount} ảnh ở ${ROUND.draft.short}, chọn ${finalCount} ảnh để chốt ở ${ROUND.final.short} — bấm để xem ảnh đã chọn`}
            onClick={onCounterClick}
            className="flex min-h-11 shrink-0 items-center gap-1 rounded-full border px-3 text-sm hover:text-foreground lg:min-h-8"
          >
            <span aria-hidden className="text-[#431613]">
              ♥
            </span>
            <span className="tabular-nums">
              {ROUND.draft.short} {draftCount} · {ROUND.final.short} {finalCount}
            </span>
          </button>
        ) : null}
      </div>
      {showStage ? (
        <div className="mx-auto max-w-6xl px-4 pb-2">
          <SelectionStageStrip stage={stage} locked={locked} onStageChange={onStageChange} />
        </div>
      ) : null}
    </header>
  );
}

/** Dải bước chọn "Nháp → Cuối" — bản GNPF của RoundStrip bên PHOTO. */
export function SelectionStageStrip({
  stage,
  locked,
  onStageChange,
}: {
  stage: SelectionStage;
  locked: boolean;
  onStageChange: (stage: SelectionStage) => void;
}) {
  const entries: Array<{ key: SelectionStage; label: string }> = [
    { key: "draft", label: ROUND.draft.step },
    { key: "final", label: ROUND.final.step },
  ];
  return (
    <nav
      aria-label="Các bước chọn ảnh"
      className="-mx-4 flex items-center gap-1 overflow-x-auto px-4 py-1"
    >
      {entries.map((entry, index) => {
        const active = !locked && entry.key === stage;
        return (
          <span key={entry.key} className="flex shrink-0 items-center gap-1">
            {index > 0 ? (
              <span aria-hidden className="text-muted-foreground">
                →
              </span>
            ) : null}
            <button
              type="button"
              disabled={locked}
              aria-current={active ? "step" : undefined}
              onClick={() => onStageChange(entry.key)}
              className={`min-h-11 rounded-full border px-3 text-sm whitespace-nowrap transition-colors lg:min-h-8 ${
                active
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground enabled:hover:text-foreground"
              } disabled:cursor-default disabled:opacity-70`}
            >
              {entry.label}
              {/* Dấu ✓ chỉ trang trí — trạng thái khoá đã có chữ "Danh sách đã khoá" bên cạnh. */}
              {locked ? <span aria-hidden> ✓</span> : null}
              {active ? <span className="text-xs"> (đang)</span> : null}
            </button>
          </span>
        );
      })}
      {locked ? (
        <span className="ml-1 shrink-0 text-xs text-muted-foreground">Danh sách đã khoá</span>
      ) : null}
    </nav>
  );
}

/**
 * Album con dưới dạng chip viên thuốc, cuộn ngang trong chính nó.
 *
 * CHỈ hiện album `collection` — thứ studio tự tạo và tự đặt tên (VD "Buổi sáng", "Concept A").
 * Thư mục `stage` (1. Ảnh gốc → 2. Khách chọn nháp → 3. Khách chốt → 4. Đang sửa →
 * 5. Ảnh hoàn thiện) là 5 chặng
 * quy trình NỘI BỘ, hệ thống tự sinh cho mọi album từ migration 0121 — khách không có việc gì
 * với chúng, mà tên chúng lại trùng nghĩa với chip lọc bên dưới nên sinh ra đúng cái cảnh "hai
 * dòng nút giống hệt nhau, hai chữ Tất cả" mà CEO bắt bỏ.
 *
 * Ẩn chip KHÔNG giấu ảnh nào: chip đầu luôn là album gốc, và album gốc chứa toàn bộ ảnh của
 * gallery (xem `albumAssets` trong CustomerGalleryPage). Đây thuần tuý là bớt lối tắt điều hướng
 * mà khách không dùng tới.
 */
export function CustomerAlbumStrip({
  albums,
  activeAlbumId,
  counts,
  onAlbumChange,
}: {
  albums: PublicGalleryAlbum[];
  activeAlbumId: string;
  counts: Map<string, number>;
  onAlbumChange: (albumId: string) => void;
}) {
  const visible = albums.filter((album, index) => index === 0 || album.kind === "collection");
  if (visible.length <= 1) return null;
  return (
    <nav aria-label="Album ảnh" className="-mx-4 flex gap-2 overflow-x-auto px-4">
      {visible.map((album, index) => (
        <button
          key={album.id}
          type="button"
          aria-current={album.id === activeAlbumId ? "page" : undefined}
          // aria-label riêng để không trùng tên với chip lọc "Tất cả N" bên toolbar.
          aria-label={`Album ${index === 0 ? "Tất cả" : album.name} — ${counts.get(album.id) ?? 0} ảnh`}
          onClick={() => onAlbumChange(album.id)}
          className={chipClass(album.id === activeAlbumId)}
        >
          <span className="max-w-44 truncate font-medium">
            {index === 0 ? "Tất cả" : album.name}
          </span>
          <span
            className={`text-xs tabular-nums ${
              album.id === activeAlbumId ? "text-primary-foreground/70" : "text-muted-foreground"
            }`}
          >
            {counts.get(album.id) ?? 0}
          </span>
        </button>
      ))}
    </nav>
  );
}

export function CustomerGalleryToolbar({
  filter,
  counts,
  sort,
  stage,
  canSelect,
  canDownload,
  downloadLockReason,
  downloadCounts,
  downloading,
  onFilterChange,
  onSortChange,
  onRefresh,
  onDownload,
}: {
  filter: GalleryFilter;
  counts: GalleryCounts;
  sort: GallerySort;
  stage: SelectionStage;
  canSelect: boolean;
  canDownload: boolean;
  /**
   * Vì sao nút tải đang khoá, nói nguyên văn cho khách. Rỗng = không khoá vì
   * tiền (link vốn không cho tải thì cũng không có gì để giải thích).
   */
  downloadLockReason?: string;
  /** Số ảnh của TOÀN album (nút tải luôn đóng gói cả gallery, không theo album con đang xem). */
  downloadCounts: { all: number; draft: number; final: number };
  downloading: boolean;
  onFilterChange: (filter: GalleryFilter) => void;
  onSortChange: (sort: GallerySort) => void;
  onRefresh: () => void;
  onDownload: (scope: DownloadScope) => void;
}) {
  return (
    <div role="toolbar" aria-label="Lọc và sắp xếp ảnh" className="space-y-3">
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4">
        {buildFilters(stage, canSelect).map(({ value, label }) => (
          <button
            key={value}
            type="button"
            aria-label={`${label} ${counts[value]}`}
            aria-pressed={filter === value}
            onClick={() => onFilterChange(value)}
            className={chipClass(filter === value)}
          >
            {label}
            <span
              className={`text-xs tabular-nums ${
                filter === value ? "text-primary-foreground/70" : "text-muted-foreground"
              }`}
            >
              {counts[value]}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={sort} onValueChange={(value) => onSortChange(value as GallerySort)}>
          <SelectTrigger aria-label="Sắp xếp ảnh" className="h-11 w-[172px] lg:h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Mới nhất</SelectItem>
            <SelectItem value="oldest">Cũ nhất</SelectItem>
            <SelectItem value="name-asc">Tên ảnh A-Z</SelectItem>
            <SelectItem value="name-desc">Tên ảnh Z-A</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            aria-label="Làm mới"
            className="rounded-full md:min-h-11 md:min-w-11 lg:min-h-9 lg:min-w-9"
            onClick={onRefresh}
          >
            <RefreshCw />
          </Button>
          {canDownload ? (
            <DownloadControl
              counts={downloadCounts}
              canSelect={canSelect}
              downloading={downloading}
              onDownload={onDownload}
            />
          ) : downloadLockReason ? (
            // Nút vẫn HIỆN nhưng khoá, kèm lý do (CEO chốt 1a 26/09/2026).
            // Giấu nút đi thì khách hỏi "sao không tải được" và nhân viên phải
            // đoán; nói thẳng thì khách biết mình cần làm gì.
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              title={downloadLockReason}
              aria-label={`Tải ảnh — ${downloadLockReason}`}
              className="rounded-full"
            >
              <Download />
              <span className="ml-1">Tải ảnh</span>
            </Button>
          ) : null}
        </div>
      </div>

      {/* Lý do khoá viết THÀNH CHỮ, không chỉ nằm trong tooltip: khách xem trên
          điện thoại không rê chuột được, nút khoá mà không chữ thì y như nút
          hỏng. Đặt ở đây (không nhét vào hàng nút) để không bóp méo hàng đó. */}
      {!canDownload && downloadLockReason ? (
        <p className="text-sm text-muted-foreground">{downloadLockReason}</p>
      ) : null}
    </div>
  );
}

/**
 * Nút tải — LUÔN là một menu, kể cả khi khách chưa thả tim tấm nào (CEO chốt 27/08/2026).
 *
 * Bản đầu chỉ hiện menu sau khi đã thả tim, còn lại là nút "Tải tất cả" phẳng. Gọn hơn, nhưng
 * CEO mở album ra ở trạng thái 0 ảnh và KHÔNG hề biết là có tính năng tải riêng — khách cũng vậy.
 * Nên giờ mục của từng vòng luôn nằm trong menu, chỉ mờ đi và ghi (0) khi chưa chọn: bản thân
 * dòng chữ mờ đó là lời mời "thả tim rồi tải riêng được". Đừng rút gọn lại thành nút phẳng.
 */
function DownloadControl({
  counts,
  canSelect,
  downloading,
  onDownload,
}: {
  counts: { all: number; draft: number; final: number };
  canSelect: boolean;
  downloading: boolean;
  onDownload: (scope: DownloadScope) => void;
}) {
  // Album chỉ-cho-xem (khách không được thả tim): hai mục vòng sẽ mờ VĨNH VIỄN, nên bỏ hẳn
  // thay vì treo hai dòng khách không bao giờ bấm được.
  const rounds: Array<{ scope: DownloadScope; label: string; count: number }> = canSelect
    ? [
        { scope: "draft", label: `Ảnh ${ROUND.draft.short}`, count: counts.draft },
        { scope: "final", label: `Ảnh ${ROUND.final.short}`, count: counts.final },
      ]
    : [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="rounded-full md:min-h-11 lg:min-h-9"
          disabled={downloading}
        >
          <Download /> Tải ảnh <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onDownload("all")}>
          Tất cả ảnh ({counts.all})
        </DropdownMenuItem>
        {rounds.map((round) => (
          <DropdownMenuItem
            key={round.scope}
            disabled={round.count === 0}
            onSelect={() => onDownload(round.scope)}
          >
            {round.label} ({round.count})
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
